import { useState, useRef, useEffect } from 'react';
import { Download, FileSpreadsheet, Layers } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { exportBoardToXlsx, exportAllBoardsToXlsx } from '../../utils/exportUtils';

interface ExportMenuProps {
  boardId: string;
}

export default function ExportMenu({ boardId }: ExportMenuProps) {
  const { state } = useApp();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const getExportParams = () => ({
    tasks: state.tasks,
    users: state.users,
    boards: state.boards,
    teams: state.teams,
    labels: state.labels,
    sprints: state.sprints,
  });

  const handleExportBoard = (includeSubtasks: boolean) => {
    const board = state.boards.find((b) => b.id === boardId);
    const boardName = board ? board.name : 'board';
    const suffix = includeSubtasks ? '_completo' : '';
    exportBoardToXlsx({
      ...getExportParams(),
      boardId,
      fileName: `${boardName}${suffix}.xlsx`,
      includeSubtasks,
    });
    setOpen(false);
  };

  const handleExportAll = (includeSubtasks: boolean) => {
    const suffix = includeSubtasks ? '_completo' : '';
    exportAllBoardsToXlsx({
      ...getExportParams(),
      fileName: `todos_boards${suffix}.xlsx`,
      includeSubtasks,
    });
    setOpen(false);
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        aria-label="Exportar"
      >
        <Download size={18} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-72 bg-white rounded-xl shadow-lg border border-slate-200 py-1 animate-fade-in">
          <button
            type="button"
            onClick={() => handleExportBoard(false)}
            className="w-full flex items-center gap-3 py-2 px-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
          >
            <FileSpreadsheet size={16} className="text-emerald-500 flex-shrink-0" />
            Exportar tarefas deste board
          </button>
          <button
            type="button"
            onClick={() => handleExportBoard(true)}
            className="w-full flex items-center gap-3 py-2 px-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
          >
            <FileSpreadsheet size={16} className="text-emerald-500 flex-shrink-0" />
            Exportar tarefas e subtarefas deste board
          </button>
          <div className="border-t border-slate-100 my-1" />
          <button
            type="button"
            onClick={() => handleExportAll(false)}
            className="w-full flex items-center gap-3 py-2 px-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
          >
            <Layers size={16} className="text-brand-500 flex-shrink-0" />
            Exportar todos os boards
          </button>
          <button
            type="button"
            onClick={() => handleExportAll(true)}
            className="w-full flex items-center gap-3 py-2 px-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
          >
            <Layers size={16} className="text-brand-500 flex-shrink-0" />
            Exportar tudo (boards + subtarefas)
          </button>
        </div>
      )}
    </div>
  );
}
